import React, { useState } from 'react';
import { X, Package as PkgIcon, Truck } from 'lucide-react';

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - data: {
 *      items: Array<{ sku: string, title: string, quantity: number }>,
 *      deliveries: Array<{
 *        delivery_point_id: number,
 *        recipient_name: string,
 *        address: string,
 *        packages: Array<{
 *          package_id: number,
 *          tracking_code: string,
 *          items: Array<{ sku: string, title: string, quantity: number }>
 *        }>
 *      }>,
 *      counts?: { deliveries: number, packages: number, units: number }
 *    }
 */
function DeliveryInventoryDialog({ open, onClose, data }) {
  const [tab, setTab] = useState('summary'); // 'summary' | 'deliveries'
  if (!open) return null;

  const items = data?.items || [];
  const deliveries = data?.deliveries || [];
  const totalUnits =
    data?.counts?.units ?? items.reduce((sum, it) => sum + (it.quantity || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#B1336E] flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-extrabold text-[#B1336E] font-montserrat">
                Entregas de Hoy
              </h2>
              <p className="text-sm text-slate-600 font-montserrat">
                Productos para enviar a Fast Max
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4">
          <div className="inline-flex bg-slate-100 rounded-xl p-1">
            <button
              onClick={() => setTab('summary')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg font-montserrat ${
                tab === 'summary'
                  ? 'bg-white shadow text-[#B1336E]'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Resumen
            </button>
            <button
              onClick={() => setTab('deliveries')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg font-montserrat ${
                tab === 'deliveries'
                  ? 'bg-white shadow text-[#B1336E]'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              Por entrega
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'summary' ? (
            <>
              {items.length > 0 ? (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block">
                    <table className="w-full">
                      <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                            SKU
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                            Producto
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase tracking-wider font-montserrat">
                            Cantidad
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {items.map((item, idx) => (
                          <tr key={`${item.sku}-${idx}`} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-900 font-montserrat font-medium">
                                {item.sku || '—'}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm text-slate-700 font-montserrat">
                                {item.title || item.sku || 'Sin título'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="inline-flex items-center justify-center px-3 py-1 bg-[#67A43C]/10 text-[#67A43C] text-sm font-bold rounded-full font-montserrat">
                                {item.quantity}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="sm:hidden space-y-3">
                    {items.map((item, idx) => (
                      <div
                        key={`${item.sku}-${idx}`}
                        className="bg-slate-50 rounded-xl p-4 border border-slate-200"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="text-xs text-slate-500 font-montserrat mb-1">
                              SKU
                            </div>
                            <div className="text-sm font-semibold text-slate-900 font-montserrat">
                              {item.sku || '—'}
                            </div>
                          </div>
                          <span className="px-3 py-1 bg-[#67A43C]/10 text-[#67A43C] text-sm font-bold rounded-full font-montserrat">
                            {item.quantity}
                          </span>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 font-montserrat mb-1">
                            Producto
                          </div>
                          <div className="text-sm text-slate-700 font-montserrat">
                            {item.title || item.sku || 'Sin título'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Summary */}
                  <div className="mt-6 pt-4 border-t border-slate-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-slate-600 font-montserrat">
                        Total de productos (SKU)
                      </span>
                      <span className="text-lg font-bold text-[#B1336E] font-montserrat">
                        {items.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-sm font-medium text-slate-600 font-montserrat">
                        Total de unidades
                      </span>
                      <span className="text-lg font-bold text-[#B1336E] font-montserrat">
                        {totalUnits}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <EmptyState />
              )}
            </>
          ) : (
            <>
              {deliveries.length > 0 ? (
                <div className="space-y-4">
                  {deliveries.map((dp) => (
                    <div key={dp.delivery_point_id} className="rounded-xl border border-slate-200">
                      <div className="px-4 py-3 bg-slate-50 rounded-t-xl flex items-center justify-between">
                        <div>
                          <div className="font-bold text-slate-900 font-montserrat">
                            {dp.recipient_name || 'Destinatario'}
                          </div>
                          <div className="text-sm text-slate-600 font-montserrat">
                            {dp.address}
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 font-montserrat">
                          DP #{dp.delivery_point_id}
                        </div>
                      </div>

                      <div className="p-4 space-y-4">
                        {dp.packages.map((pkg) => (
                          <div key={pkg.package_id} className="rounded-lg border border-slate-200">
                            <div className="px-3 py-2 bg-white flex items-center justify-between rounded-t-lg">
                              <div className="text-sm font-semibold text-slate-800 font-montserrat">
                                Paquete #{pkg.package_id}
                              </div>
                              <div className="text-xs text-slate-500 font-montserrat">
                                {pkg.tracking_code}
                              </div>
                            </div>

                            <div className="p-3 bg-slate-50 rounded-b-lg">
                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-xs uppercase text-slate-600 font-bold font-montserrat">
                                      <th className="text-left px-2 py-2">SKU</th>
                                      <th className="text-left px-2 py-2">Producto</th>
                                      <th className="text-right px-2 py-2">Cantidad</th>
                                    </tr>
                                  </thead>
                                  <tbody className="text-sm">
                                    {pkg.items.map((it, idx) => (
                                      <tr key={`${it.sku}-${idx}`} className="border-t border-slate-200">
                                        <td className="px-2 py-2">{it.sku || '—'}</td>
                                        <td className="px-2 py-2">{it.title || it.sku || 'Sin título'}</td>
                                        <td className="px-2 py-2 text-right">
                                          <span className="inline-flex items-center justify-center px-2 py-0.5 bg-[#67A43C]/10 text-[#67A43C] text-xs font-bold rounded-full font-montserrat">
                                            {it.quantity}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState />
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors duration-200 font-montserrat"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <PkgIcon className="w-16 h-16 text-slate-300 mb-4" />
      <h3 className="text-lg font-bold text-slate-900 font-montserrat mb-2">
        No hay entregas para hoy
      </h3>
      <p className="text-sm text-slate-600 font-montserrat text-center">
        Los productos programados para entrega aparecerán aquí
      </p>
    </div>
  );
}

export default DeliveryInventoryDialog;
